import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import Header from '../components/Header.tsx';
import MenuSection from '../components/MenuSection.tsx';
import YapePayment from '../components/YapePayment.tsx';
import { Product } from '../data/menuData.ts';
import { SHOW_MENU_ORDERING } from '../config/ui.ts';

const MenuPageContainer = styled.div`
  min-height: 100vh;
  background: var(--white);
  position: relative;
`;

const BackButton = styled.button`
  position: fixed;
  top: 100px;
  left: 20px;
  background: var(--navy-blue);
  color: var(--brand-gold);
  border: none;
  padding: 12px 20px;
  border-radius: 25px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 1000;
  display: flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 5px 15px rgba(0, 31, 63, 0.3);
  
  &:hover {
    background: var(--brand-gold);
    color: var(--navy-blue);
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(211, 141, 84, 0.35);
  }
  
  i {
    font-size: 1rem;
  }
  
  @media (max-width: 768px) {
    top: 85px;
    left: 15px;
    padding: 10px 15px;
    font-size: 0.9rem;
  }
`;

const CartButton = styled.button`
  position: fixed;
  bottom: 30px;
  right: 30px;
  background: linear-gradient(135deg, #722F7F, #9C4A9F);
  color: var(--white);
  border: none;
  padding: 15px 25px;
  border-radius: 50px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 1000;
  display: flex;
  align-items: center;
  gap: 10px;
  box-shadow: 0 10px 30px rgba(114, 47, 127, 0.3);
  font-size: 1.1rem;
  
  &:hover {
    transform: translateY(-3px) scale(1.05);
    box-shadow: 0 15px 40px rgba(114, 47, 127, 0.4);
  }
  
  @media (max-width: 768px) {
    bottom: 20px;
    right: 20px;
    padding: 12px 20px;
    font-size: 1rem;
  }
`;

const CartCount = styled.span`
  background: rgba(227, 227, 221, 0.25);
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 0.9rem;
  min-width: 20px;
  text-align: center;
`;

interface CartItem {
  id: string;
  name: string;
  price: number;
  quantity: number;
}

interface MenuPageProps {}

const MenuPage: React.FC<MenuPageProps> = () => {
  const { location } = useParams<{ location: string }>();
  const navigate = useNavigate();
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [isPaymentOpen, setIsPaymentOpen] = useState(false);

  const handleBackClick = () => {
    navigate('/', { state: { scrollTo: 'locales' } });
  };

  const addToCart = (product: Product, options: any, quantity: number) => {
    if (!SHOW_MENU_ORDERING) return;
    const newItem: CartItem = {
      id: `${product.id}-${Date.now()}`,
      name: product.name,
      price: product.price,
      quantity
    };

    setCartItems(prev => {
      const existingItem = prev.find(item => item.name === product.name);
      if (existingItem) {
        return prev.map(item => 
          item.name === product.name 
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }
      return [...prev, newItem];
    });
  };

  const getTotalItems = () => {
    return cartItems.reduce((total, item) => total + item.quantity, 0);
  };

  const getTotalPrice = () => {
    return cartItems.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const handlePaymentComplete = () => {
    setCartItems([]);
    setIsPaymentOpen(false);
  };

  const handleContinueShopping = () => {
    setIsPaymentOpen(false);
    // El usuario puede seguir agregando productos sin cerrar la página
  };

  const handleUpdateQuantity = (id: string, newQuantity: number) => {
    if (newQuantity <= 0) {
      handleRemoveItem(id);
      return;
    }
    
    setCartItems(prev => 
      prev.map(item => 
        item.id === id 
          ? { ...item, quantity: newQuantity }
          : item
      )
    );
  };

  const handleRemoveItem = (id: string) => {
    setCartItems(prev => prev.filter(item => item.id !== id));
  };

  const handleCartClick = () => {
    if (!SHOW_MENU_ORDERING) return;
    if (cartItems.length > 0) {
      setIsPaymentOpen(true);
    }
  };

  const getLocationName = (locationParam: string | undefined) => {
    if (!locationParam) return 'Cayma';
    
    const locationMap: { [key: string]: string } = {
      'selva-alegre': 'Selva Alegre',
      'cercado': 'Cercado',
      'cayma-micaela-bastidas': 'Cayma (Micaela Bastidas)',
      'cayma-ramon-castilla': 'Cayma (Ramón Castilla)'
    };
    
    return locationMap[locationParam] || 'Cayma';
  };

  return (
    <MenuPageContainer>
      <Header />
      {false && (
        <BackButton onClick={handleBackClick}>
          <i className="fas fa-arrow-left"></i>
          Volver a Locales
        </BackButton>
      )}
      <MenuSection 
        selectedLocation={getLocationName(location)}
        onAddToCart={SHOW_MENU_ORDERING ? addToCart : undefined}
      />
      
      {SHOW_MENU_ORDERING && getTotalItems() > 0 && (
        <CartButton onClick={handleCartClick}>
          <i className="fas fa-shopping-cart"></i>
          Pagar con YAPE
          <CartCount>{getTotalItems()}</CartCount>
        </CartButton>
      )}

      {SHOW_MENU_ORDERING && (
        <YapePayment
          isOpen={isPaymentOpen}
          onClose={() => setIsPaymentOpen(false)}
          cartItems={cartItems}
          total={getTotalPrice()}
          onPaymentComplete={handlePaymentComplete}
          onContinueShopping={handleContinueShopping}
          selectedLocation={getLocationName(location)}
          onUpdateQuantity={handleUpdateQuantity}
          onRemoveItem={handleRemoveItem}
        />
      )}
    </MenuPageContainer>
  );
};

export default MenuPage;